import config from 'config';

import { handleResponse } from 'utils/Api/common';

const {
  constants: {
    API: { PS_CORE_BASE_URL },
  },
} = config;

/**
 * Per-contact notes and documents live in propstream-core's investor-service,
 * behind the core gateway -- not the PS4 backend.
 *
 * These go out as plain fetches rather than through the CALL_API middleware, on
 * the same pattern as data/user/apiService.js. That middleware queues requests
 * by `method + path + query` with maxCount 1 and aborts the older duplicate by
 * rejecting its promise without running either callback, which is easy to hang
 * a UI on. A direct call has one obvious outcome: it resolves or it throws.
 *
 * `/ps-core/contacts/{id}/...` is a path alias for `/ps-core/investors/{id}/...`
 * -- same controller, same rows. Auth needs nothing special: the gateway trades
 * the x-auth-token for the X-User-Id the service reads.
 */
const authHeaders = () => ({
  'Content-Type': 'application/json',
  'x-auth-token': window.appAuthToken,
});

// `prefix` defaults to `contacts` -- the homeowner Contact Details editor's
// own context. The Investor Profile passes `investors` instead so its notes
// calls read as /ps-core/investors/{id}/notes, matching the URL it already
// uses for investor-statuses/touchpoints/save-snapshot -- ContactNoteController
// itself treats the two prefixes identically (see this file's header
// comment), so this only changes which URL each caller's own context sees,
// not what data comes back.
const contactUrl = (contactId, resource, prefix = 'contacts') =>
  `${PS_CORE_BASE_URL}/ps-core/${prefix}/${contactId}/${resource}`;

export const fetchNotes = async (contactId, prefix) => {
  const response = await fetch(contactUrl(contactId, 'notes', prefix), {
    method: 'GET',
    headers: authHeaders(),
  });

  return handleResponse(response);
};

export const postNote = async (contactId, note, prefix) => {
  const response = await fetch(contactUrl(contactId, 'notes', prefix), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ note }),
  });

  return handleResponse(response);
};

export const fetchDocuments = async (contactId) => {
  const response = await fetch(contactUrl(contactId, 'documents'), {
    method: 'GET',
    headers: authHeaders(),
  });

  return handleResponse(response);
};

export const postDocument = async (contactId, file) => {
  const body = new FormData();
  // the part name has to be `file` -- ContactDocumentController reads
  // @RequestParam("file"). No Content-Type here: the browser sets the
  // multipart boundary itself.
  body.append('file', file, file.name);

  const response = await fetch(contactUrl(contactId, 'documents'), {
    method: 'POST',
    headers: { 'x-auth-token': window.appAuthToken },
    body,
  });

  return handleResponse(response);
};

export const deleteDocument = async (contactId, documentId) => {
  const response = await fetch(contactUrl(contactId, `documents/${documentId}`), {
    method: 'DELETE',
    headers: authHeaders(),
  });

  return handleResponse(response);
};

// ATAI-114 / S8: Investor Status & Touchpoint. Same contactUrl helper, same
// ContactAccessService-backed endpoints as Notes/Documents above -- just
// reachable under the sibling `/ps-core/investors/...` alias too (see this
// file's own header comment on the alias), not used here since this file
// always calls through the `/contacts` prefix.
//
// GET /ps-core/contacts/investor-statuses[?contactId=] -- the fixed enum
// list, now { id, name, seq, selected }[]. `selected` is only meaningful when
// `contactId` is passed: it flags the one row (if any) this contact currently
// has saved, replacing the old separate `/workflow` GET (removed -- ATAI-114
// follow-up). Omit `contactId` to just list the options.
export const fetchInvestorStatuses = async (contactId) => {
  const url = `${PS_CORE_BASE_URL}/ps-core/contacts/investor-statuses${
    contactId ? `?contactId=${contactId}` : ''
  }`;
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  return handleResponse(response);
};

// GET /ps-core/contacts/touchpoints[?contactId=] -- same shape as above.
export const fetchTouchpoints = async (contactId) => {
  const url = `${PS_CORE_BASE_URL}/ps-core/contacts/touchpoints${
    contactId ? `?contactId=${contactId}` : ''
  }`;
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  return handleResponse(response);
};

// PUT /ps-core/contacts/{contactId}/investor-status -- sets this contact's
// Investor Status; `id: null` clears it. Returns the resulting
// { contactId, investorStatusId, touchpointId }.
export const putInvestorStatus = async (contactId, investorStatusId) => {
  const response = await fetch(contactUrl(contactId, 'investor-status'), {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ id: investorStatusId }),
  });

  return handleResponse(response);
};

// PUT /ps-core/contacts/{contactId}/touchpoint -- same shape as above.
export const putTouchpoint = async (contactId, touchpointId) => {
  const response = await fetch(contactUrl(contactId, 'touchpoint'), {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ id: touchpointId }),
  });

  return handleResponse(response);
};
